import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui';
import { trackEvent } from '../utils/analytics';
import axios from 'axios';
import { auth } from '../config/firebase';

// Step 1: Welcome
const WelcomeStep = ({ onNext }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    className="text-center"
  >
    {/* Ghost icon with floating animation */}
    <motion.div
      animate={{ y: [0, -10, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      className="text-6xl mb-6"
    >
      👻
    </motion.div>

    <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
      Welcome to Boop!
    </h2>

    <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-md mx-auto">
      Discover, rate, and improve AI prompts with our vibrant community.
      Let's get you started!
    </p>

    <Button onClick={onNext} variant="primary" size="lg">
      Get Started →
    </Button>
  </motion.div>
);

// Step 2: Interests Selection
const InterestsStep = ({ selectedInterests, setSelectedInterests, onNext, onBack }) => {
  const categories = [
    { id: 'coding', name: 'Coding', icon: '💻', color: 'bg-blue-500' },
    { id: 'writing', name: 'Writing', icon: '✍️', color: 'bg-purple-500' },
    { id: 'marketing', name: 'Marketing', icon: '📈', color: 'bg-green-500' },
    { id: 'business', name: 'Business', icon: '💼', color: 'bg-yellow-500' },
    { id: 'creative', name: 'Creative', icon: '🎨', color: 'bg-pink-500' },
    { id: 'education', name: 'Education', icon: '📚', color: 'bg-indigo-500' },
    { id: 'productivity', name: 'Productivity', icon: '⚡', color: 'bg-orange-500' },
    { id: 'research', name: 'Research', icon: '🔬', color: 'bg-teal-500' },
  ];

  const toggleInterest = (id) => {
    setSelectedInterests(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const canContinue = selectedInterests.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white text-center">
        What interests you?
      </h2>
      <p className="text-gray-600 dark:text-gray-300 mb-6 text-center">
        Select at least one category to personalize your experience
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => toggleInterest(category.id)}
            className={`
              p-4 rounded-lg border-2 transition-all duration-200
              ${selectedInterests.includes(category.id)
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 transform scale-105'
                : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600'
              }
            `}
          >
            <div className="text-3xl mb-2">{category.icon}</div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {category.name}
            </div>
          </button>
        ))}
      </div>

      <div className="flex gap-4">
        <Button onClick={onBack} variant="secondary">
          ← Back
        </Button>
        <Button
          onClick={onNext}
          variant="primary"
          disabled={!canContinue}
          className="flex-1"
        >
          Continue →
        </Button>
      </div>
    </motion.div>
  );
};

// Step 3: How It Works
const HowItWorksStep = ({ onFinish, onBack }) => {
  const features = [
    {
      icon: '⭐',
      title: 'Multi-Dimensional Ratings',
      description: 'Rate prompts on Clarity, Creativity, Effectiveness, and Overall quality',
    },
    {
      icon: '🏆',
      title: 'Gamification System',
      description: 'Level up from Beginner to Professional, earn badges, and win weekly awards',
    },
    {
      icon: '📚',
      title: 'Collections',
      description: 'Save your favorite prompts and organize them into custom collections',
    },
    {
      icon: '🔀',
      title: 'Fork & Improve',
      description: 'Build on existing prompts and collaborate with the community',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white text-center">
        How Boop Works
      </h2>
      <p className="text-gray-600 dark:text-gray-300 mb-6 text-center">
        Here's what makes Boop unique
      </p>

      <div className="space-y-4 mb-8">
        {features.map((feature, index) => (
          <motion.div
            key={feature.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
          >
            <div className="text-3xl flex-shrink-0">{feature.icon}</div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                {feature.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {feature.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="flex gap-4">
        <Button onClick={onBack} variant="secondary">
          ← Back
        </Button>
        <Button onClick={onFinish} variant="primary" className="flex-1">
          Start Exploring! 🚀
        </Button>
      </div>
    </motion.div>
  );
};

// Main OnboardingModal Component
const OnboardingModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const steps = [
    { component: WelcomeStep, props: { onNext: () => handleNext() } },
    {
      component: InterestsStep,
      props: {
        selectedInterests,
        setSelectedInterests,
        onNext: () => handleNext(),
        onBack: () => handleBack(),
      },
    },
    {
      component: HowItWorksStep,
      props: {
        onFinish: () => handleFinish(),
        onBack: () => handleBack(),
      },
    },
  ];

  const CurrentStep = steps[currentStep];
  const CurrentStepComponent = CurrentStep.component;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      trackEvent('Onboarding Step Completed', { step: currentStep + 1 });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    setIsSaving(true);
    try {
      // Save interests to backend if any were selected
      if (selectedInterests.length > 0) {
        const token = await auth.currentUser.getIdToken();
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/auth/interests`,
          { interests: selectedInterests },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      // Mark onboarding as complete
      localStorage.setItem('onboarding_completed', 'true');

      // Track completion
      trackEvent('Onboarding Completed', {
        interests: selectedInterests.join(','),
        interests_count: selectedInterests.length,
        steps_completed: 3,
      });

      onClose();
    } catch (error) {
      console.error('Error saving interests:', error);
      // Don't block user - still close modal and mark complete
      localStorage.setItem('onboarding_completed', 'true');
      trackEvent('Onboarding Completed with Error', {
        error: error.message,
        step: 'finish',
      });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding_completed', 'true');
    trackEvent('Onboarding Skipped', { step: currentStep + 1 });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full p-8 relative max-h-[90vh] overflow-y-auto"
      >
        {/* Skip button */}
        <button
          onClick={handleSkip}
          disabled={isSaving}
          className="absolute top-4 right-4 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
        >
          Skip
        </button>

        {/* Progress indicator */}
        <div className="flex justify-center gap-2 mb-8">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentStep
                  ? 'w-8 bg-purple-500'
                  : index < currentStep
                  ? 'w-2 bg-purple-500'
                  : 'w-2 bg-gray-300 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>

        {/* Step content */}
        <AnimatePresence mode="wait">
          <CurrentStepComponent key={currentStep} {...CurrentStep.props} />
        </AnimatePresence>

        {/* Loading overlay */}
        {isSaving && (
          <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 flex items-center justify-center rounded-2xl">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300">Saving your preferences...</p>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default OnboardingModal;
